/**
 * CompetitorList — Sprint 2.6
 *
 * Drag-reorder list of competitor suggestions with badge/rationale display,
 * accept/reject, manual add, and auto-save throughout. No explicit save button.
 *
 * Desktop: drag-handle on left. Mobile: up/down buttons.
 */
import { useState, useRef, useEffect } from 'react';
import { api } from '../api';
import type { CompetitorSuggestion } from '../types';

const BADGE_LABELS: Record<string, string> = {
  direct:     'Direct',
  serp_rival: 'SERP rival',
  llm_rival:  'LLM rival',
  adjacent:   'Adjacent',
  local_rival:'Local rival',
};

const BADGE_COLOURS: Record<string, string> = {
  direct:     'bg-red-50 text-red-700 border-red-200',
  serp_rival: 'bg-amber-50 text-amber-700 border-amber-200',
  llm_rival:  'bg-purple-50 text-purple-700 border-purple-200',
  adjacent:   'bg-blue-50 text-blue-700 border-blue-200',
  local_rival:'bg-teal-50 text-teal-700 border-teal-200',
};

interface Props {
  ownedDomainId: string;
  /** Number of tracked-domain slots still available on the customer's tier */
  availableSlots: number;
  /**
   * Business-context confidence score (0-100). When provided and < 60 the
   * refresh button is hidden entirely (the gate would block the request anyway).
   */
  businessContextConfidence?: number | null;
  /**
   * Called after every accepted or manually-added competitor. Callers may
   * use this to flip `competitor_chosen` in the onboarding progress.
   */
  onCompetitorAdded?: () => void;
  /**
   * Override the empty-state body text (below the heading). Defaults to the
   * beacon-oriented copy shown in the dashboard CompetitorsTab.
   */
  emptyStateMessage?: string;
}

export function CompetitorList({ ownedDomainId, availableSlots, businessContextConfidence, onCompetitorAdded, emptyStateMessage }: Props) {
  const [items, setItems] = useState<CompetitorSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [addValue, setAddValue] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [undoStack, setUndoStack] = useState<CompetitorSuggestion[]>([]);
  const [undoTimer, setUndoTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const dragIndex = useRef<number | null>(null);

  useEffect(() => {
    api.competitors
      .list(ownedDomainId)
      .then((data) => setItems(data.filter((s) => s.status !== 'rejected')))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [ownedDomainId]);

  // ── Helpers ────────────────────────────────────────────────────────────

  function flash(msg = 'Saved') {
    setSavedMsg(msg);
    setTimeout(() => setSavedMsg(''), 2000);
  }

  const activeItems    = items.filter((s) => s.status !== 'rejected');
  const suggestedItems = activeItems.filter((s) => s.status === 'suggested');
  const selectedItems  = activeItems.slice(0, availableSlots);
  const extraItems     = activeItems.slice(availableSlots);

  // ── Accept ─────────────────────────────────────────────────────────────

  async function handleAccept(item: CompetitorSuggestion) {
    try {
      await api.competitors.updateStatus(item.id, ownedDomainId, 'accepted');
      setItems((prev) =>
        prev.map((s) => (s.id === item.id ? { ...s, status: 'accepted' } : s)),
      );
      flash('Scanning competitor...');
      onCompetitorAdded?.();
    } catch {
      setError('Could not accept competitor');
    }
  }

  // ── Remove with undo ────────────────────────────────────────────────────

  function handleRemove(item: CompetitorSuggestion) {
    // Optimistic remove
    setItems((prev) => prev.filter((s) => s.id !== item.id));
    setUndoStack((prev) => [...prev, item]);
    flash();
    // Server call after 10s
    const t = setTimeout(() => {
      api.competitors.remove(item.id).catch(() => {});
      setUndoStack((prev) => prev.filter((s) => s.id !== item.id));
    }, 10_000);
    setUndoTimer(t);
  }

  function handleUndo() {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    if (undoTimer) clearTimeout(undoTimer);
    setUndoStack((prev) => prev.slice(0, -1));
    setItems((prev) => {
      const next = [...prev, last];
      return next.sort((a, b) => a.rank - b.rank);
    });
    setSavedMsg('Restored');
    setTimeout(() => setSavedMsg(''), 2000);
  }

  // ── Reorder ────────────────────────────────────────────────────────────

  function handleDragStart(index: number) {
    dragIndex.current = index;
  }

  async function handleDrop(dropIndex: number) {
    if (dragIndex.current === null || dragIndex.current === dropIndex) return;
    const next = [...activeItems];
    const [moved] = next.splice(dragIndex.current, 1);
    if (!moved) return;
    next.splice(dropIndex, 0, moved);
    const reranked = next.map((s, i) => ({ ...s, rank: i + 1 }));
    setItems(reranked);
    dragIndex.current = null;
    try {
      await api.competitors.reorder(ownedDomainId, reranked.map((s) => s.id));
      flash();
    } catch {
      setError('Could not save order');
    }
  }

  async function moveUp(index: number) {
    if (index === 0) return;
    await handleDrop(index - 1);
  }

  async function moveDown(index: number) {
    if (index >= activeItems.length - 1) return;
    await handleDrop(index + 1);
  }
  // ── Refresh competitor discovery ───────────────────────────────────────────────

  async function handleRefresh() {
    const confirmed = window.confirm(
      'This re-runs competitor discovery. Your accepted and manually-added competitors are preserved.',
    );
    if (!confirmed) return;
    setRefreshing(true);
    setRefreshError('');
    try {
      const result = await api.competitors.refreshCompetitors(ownedDomainId);
      if ('status' in result && result.status === 'already_queued') {
        flash('Discovery already in progress.');
      } else {
        flash('Refreshing competitor suggestions...');
        // Start 30-second cooldown to prevent accidental double-clicks.
        setRefreshCooldown(true);
        setTimeout(() => setRefreshCooldown(false), 30_000);
        // Refetch the suggestion list so new candidates appear.
        const fresh = await api.competitors.list(ownedDomainId);
        setItems(fresh.filter((s) => s.status !== 'rejected'));
      }
    } catch (e) {
      // Show 422 errors inline; generic errors in the main error state.
      const msg = e instanceof Error ? e.message : 'Failed to refresh';
      setRefreshError(msg);
    } finally {
      setRefreshing(false);
    }
  }
  // ── Manual add ─────────────────────────────────────────────────────────

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const hostname = addValue.trim().replace(/^https?:\/\//i, '').replace(/\/.*/, '').replace(/^www\./, '');
    if (!hostname) return;
    if (items.some((s) => s.candidateHostname === hostname)) {
      setAddError('Already in your list');
      return;
    }
    setAdding(true);
    setAddError('');
    try {
      const created = await api.competitors.addManual(ownedDomainId, hostname);
      setItems((prev) => [...prev, created]);
      setAddValue('');
      flash();
      onCompetitorAdded?.();
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to add');
    } finally {
      setAdding(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-sm text-red-500 py-4">{error}</p>;

  // Hide refresh button if confidence is known and below gate threshold.
  const showRefreshBtn = businessContextConfidence == null || businessContextConfidence >= 60;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-brand-ink text-sm">Competitors</h3>
          {activeItems.length > 0 && (
            <p className="text-xs text-gray-400 mt-0.5">
              Drag to reorder. Top {availableSlots} appear in your weekly digest.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {savedMsg && (
            <span className="text-xs text-brand-green">{savedMsg}</span>
          )}
          {undoStack.length > 0 && (
            <button
              type="button"
              onClick={handleUndo}
              className="text-xs text-brand-green hover:underline"
            >
              Undo
            </button>
          )}
          {showRefreshBtn && (
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={refreshing || refreshCooldown}
              className="text-xs text-gray-500 hover:text-brand-ink border border-gray-200 rounded-md px-2 py-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Refresh competitor suggestions"
            >
              {refreshing ? 'Refreshing…' : '↺ Refresh suggestions'}
            </button>
          )}
        </div>
      </div>

      {/* Inline error banner for refresh 422 errors */}
      {refreshError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center justify-between">
          <span>{refreshError}</span>
          <button type="button" onClick={() => setRefreshError('')} className="ml-2 text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      {/* Empty state */}
      {activeItems.length === 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-6 text-center text-sm text-gray-400" data-testid="competitor-empty-state">
          <p className="font-medium text-brand-ink mb-1">No competitors yet</p>
          <p>{emptyStateMessage ?? 'fixmyseo will auto-suggest competitors after your first beacon. You can also add one manually below.'}</p>
        </div>
      )}

      {/* First-time framing */}
      {activeItems.length > 0 && activeItems.some((s) => s.status === 'suggested') && (
        <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          We found {activeItems.length} likely competitor{activeItems.length !== 1 ? 's' : ''} based on your keywords and LLM citations. Pick the ones you want to track.
        </p>
      )}

      {/* List */}
      <div className="space-y-1.5">
        {activeItems.map((item, index) => {
          const isSelected = index < availableSlots;
          return (
            <div
              key={item.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => void handleDrop(index)}
              className={`group flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                isSelected
                  ? 'bg-white border-gray-200'
                  : 'bg-gray-50 border-gray-100'
              }`}
            >
              {/* Drag handle (desktop) */}
              <div className="hidden sm:flex flex-col gap-0.5 pt-1 cursor-grab opacity-30 group-hover:opacity-70">
                <span className="w-3.5 h-0.5 bg-gray-500 rounded" />
                <span className="w-3.5 h-0.5 bg-gray-500 rounded" />
                <span className="w-3.5 h-0.5 bg-gray-500 rounded" />
              </div>

              {/* Up/down (mobile) */}
              <div className="flex sm:hidden flex-col gap-1">
                <button type="button" onClick={() => void moveUp(index)} className="text-gray-400 hover:text-brand-ink text-xs leading-none">▲</button>
                <button type="button" onClick={() => void moveDown(index)} className="text-gray-400 hover:text-brand-ink text-xs leading-none">▼</button>
              </div>

              {/* Rank */}
              <span className="text-xs text-gray-400 font-medium w-4 pt-0.5">{index + 1}</span>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-brand-ink truncate">
                    {item.candidateHostname}
                  </span>
                  <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${BADGE_COLOURS[item.badge] ?? ''}`}>
                    {BADGE_LABELS[item.badge] ?? item.badge}
                  </span>
                  {!isSelected && (
                    <span className="text-[11px] text-gray-400 italic">also worth considering</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{item.rationale}</p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                {item.status === 'suggested' && (
                  <button
                    type="button"
                    onClick={() => void handleAccept(item)}
                    className="text-[11px] font-medium text-brand-green hover:bg-green-50 px-2 py-1 rounded transition-colors"
                    title="Accept and start tracking"
                  >
                    Track
                  </button>
                )}
                {item.status === 'accepted' && (
                  <span className="text-[11px] text-brand-green font-medium">Tracked ✓</span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(item)}
                  className="text-gray-300 hover:text-red-400 transition-colors ml-1"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Manually add a competitor */}
      <form onSubmit={(e) => void handleAdd(e)} className="flex gap-2 pt-1">
        <input
          type="text"
          value={addValue}
          onChange={(e) => setAddValue(e.target.value)}
          placeholder="Add a competitor domain…"
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green"
        />
        <button
          type="submit"
          disabled={adding || !addValue.trim()}
          className="text-sm font-medium bg-brand-ink text-white rounded-lg px-4 py-2 hover:bg-brand-ink/90 disabled:opacity-40 transition-colors"
        >
          {adding ? '…' : 'Add'}
        </button>
      </form>
      {addError && <p className="text-xs text-red-500 -mt-2">{addError}</p>}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
    </div>
  );
}
